import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Download, Mail, CheckCircle, FileText, BookOpen, Clock, Target, GraduationCap, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';
import { analytics } from '../hooks/useAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const guides = [
  {
    slug: "parent-guide-ed",
    title: "The Parent's Guide to Early Decision",
    desc: "Understand binding vs. non-binding agreements, financial aid implications, and how to advise your child on the biggest strategic decision of the admissions process.",
    icon: BookOpen,
    tag: "For Parents",
    tagColor: "bg-amber-100 text-amber-800",
    pages: "18 pages",
  },
  {
    slug: "essay-architecture-blueprint",
    title: "Essay Architecture Blueprint",
    desc: "Our proprietary framework for structuring a personal statement that admissions officers remember. Includes 3 structural templates with annotated examples.",
    icon: FileText,
    tag: "Most Downloaded",
    tagColor: "bg-emerald-100 text-emerald-800",
    pages: "24 pages",
  },
  {
    slug: "activity-list-optimizer",
    title: "The Activity List Optimizer",
    desc: "How to present 10 activities so each one tells a different story. Includes a 150-character description formula and real before/after examples from admitted students.",
    icon: Target,
    tag: "New for 2025-26",
    tagColor: "bg-blue-100 text-blue-800",
    pages: "14 pages",
  },
  {
    slug: "financial-aid-decoded",
    title: "Financial Aid & Merit Scholarships Decoded",
    desc: "A plain-English guide to FAFSA, CSS Profile, institutional aid, and the 12 schools that meet 100% of demonstrated need. Includes a family contribution estimator.",
    icon: BarChart3,
    tag: "Essential",
    tagColor: "bg-purple-100 text-purple-800",
    pages: "22 pages",
  },
  {
    slug: "junior-year-roadmap",
    title: "The Junior Year Roadmap",
    desc: "Month-by-month breakdown of exactly what to do from September of junior year through summer before senior year. Testing dates, campus visits, essay brainstorming timeline.",
    icon: Clock,
    tag: "Start Here",
    tagColor: "bg-rose-100 text-rose-800",
    pages: "16 pages",
  },
  {
    slug: "rec-letter-strategy",
    title: "Recommender Strategy Guide",
    desc: "How to choose, approach, and brief your recommenders so they write letters that actually move the needle. Includes email templates and a 'brag sheet' worksheet.",
    icon: GraduationCap,
    tag: "Underrated",
    tagColor: "bg-teal-100 text-teal-800",
    pages: "12 pages",
  },
];

function GuideCard({ guide, onDownload }) {
  const Icon = guide.icon;
  return (
    <div className="group bg-white border border-[#E2E2E2] rounded-lg p-6 hover:border-[#0F392B]/30 hover:shadow-md transition-all" data-testid={`guide-card-${guide.slug}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="w-11 h-11 rounded-lg bg-[#0F392B]/5 flex items-center justify-center">
          <Icon className="h-5 w-5 text-[#0F392B]" />
        </div>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${guide.tagColor}`}>{guide.tag}</span>
      </div>
      <h3 className="font-semibold text-base mb-2 text-foreground leading-snug">{guide.title}</h3>
      <p className="text-sm text-muted-foreground leading-relaxed mb-4">{guide.desc}</p>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <FileText className="h-3 w-3" /> {guide.pages}
          </span>
          <span className="text-xs text-muted-foreground">PDF</span>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="text-xs h-8 border-[#0F392B]/20 text-[#0F392B] hover:bg-[#0F392B] hover:text-white transition-colors"
          onClick={() => onDownload(guide)}
          data-testid={`download-btn-${guide.slug}`}
        >
          <Download className="h-3 w-3 mr-1" /> Download Free
        </Button>
      </div>
      {/* ILC Branding Bar */}
      <div className="mt-4 pt-3 border-t border-dashed border-[#E2E2E2] flex items-center gap-2">
        <GraduationCap className="h-3.5 w-3.5 text-[#0F392B]" strokeWidth={1.5} />
        <span className="text-[10px] text-muted-foreground tracking-wide uppercase" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
      </div>
    </div>
  );
}

function EmailCaptureModal({ guide, onClose, onSuccess }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/leads/capture`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, guide_slug: guide.slug }),
      });
      if (!res.ok) throw new Error('Failed');
      onSuccess(guide);
      analytics.leadCapture('resources_page', email);
      analytics.resourceDownload(guide.slug);
      toast.success(`"${guide.title}" is ready — check your download!`);
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose} data-testid="email-capture-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-0 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header — branded */}
        <div className="bg-[#0F392B] px-6 py-5 text-white">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-xs font-medium tracking-wide uppercase" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
          </div>
          <h3 className="text-lg font-semibold leading-snug">Download: {guide.title}</h3>
          <p className="text-xs text-white/70 mt-1">Enter your email and we'll unlock instant access.</p>
        </div>
        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">First Name (optional)</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane"
              className="w-full px-3 py-2 text-sm border border-[#E2E2E2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20 focus:border-[#0F392B]"
              data-testid="lead-name-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">Email Address *</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              className="w-full px-3 py-2 text-sm border border-[#E2E2E2] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F392B]/20 focus:border-[#0F392B]"
              data-testid="lead-email-input"
            />
          </div>
          <Button
            type="submit"
            disabled={loading || !email}
            className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-10"
            data-testid="lead-submit-btn"
          >
            {loading ? 'Sending...' : 'Get Free Download'}
            {!loading && <ArrowRight className="ml-1 h-4 w-4" />}
          </Button>
          <p className="text-[10px] text-muted-foreground text-center">No spam. Unsubscribe anytime. We respect your inbox.</p>
        </form>
        <button onClick={onClose} className="absolute top-4 right-4 text-white/60 hover:text-white text-sm" data-testid="modal-close-btn">&times;</button>
      </div>
    </div>
  );
}

function DownloadSuccessView({ guide, onReset }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onReset} data-testid="download-success-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-8 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-7 w-7 text-emerald-600" />
        </div>
        <h3 className="text-lg font-semibold mb-1">You're all set!</h3>
        <p className="text-sm text-muted-foreground mb-5">Your copy of <strong>"{guide.title}"</strong> is ready.</p>
        {/* Branded document preview */}
        <div className="border border-[#E2E2E2] rounded-lg p-4 mb-5 bg-[#F9F9F7]">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap className="h-4 w-4 text-[#0F392B]" strokeWidth={1.5} />
            <span className="text-[10px] font-medium tracking-wide uppercase text-[#0F392B]" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
          </div>
          <p className="text-sm font-medium text-foreground">{guide.title}</p>
          <p className="text-xs text-muted-foreground mt-1">{guide.pages} &middot; PDF Guide</p>
        </div>
        <p className="text-xs text-muted-foreground mb-4">This is a preview resource. The full portal includes AI-powered tools, deadline tracking, and essay feedback.</p>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1 text-sm" onClick={onReset} data-testid="download-close-btn">Browse More Guides</Button>
          <Link to="/sign-up" className="flex-1">
            <Button className="w-full bg-[#0F392B] hover:bg-[#0F392B]/90 text-white text-sm" data-testid="download-portal-cta">
              Access the Portal — $99 <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function ResourcesPage() {
  const [activeGuide, setActiveGuide] = useState(null);
  const [completedGuide, setCompletedGuide] = useState(null);

  const handleDownload = (guide) => setActiveGuide(guide);
  const handleSuccess = (guide) => {
    setActiveGuide(null);
    setCompletedGuide(guide);
  };
  const handleReset = () => setCompletedGuide(null);

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="resources-page">
      <SEO
        title="Free Admissions Guides & Resources"
        description="Download free guides on Early Decision strategy, essay architecture, financial aid, and more. Built by former Ivy League admissions officers at Ivy League Consulting."
        path="/resources"
      />
      <MarketingNav />

      {/* Hero */}
      <section className="pt-28 pb-14 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <p className="text-xs font-medium text-[#0F392B] uppercase tracking-widest mb-3">Free Resources</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-foreground leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            The Guides We Wish<br className="hidden sm:block" /> We Had in High School
          </h1>
          <p className="text-base text-muted-foreground leading-relaxed max-w-lg">
            Distilled from 15+ years of admissions experience and thousands of successful applications. Enter your email to download any guide instantly — completely free.
          </p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="guides-grid">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {guides.map((g) => (
            <GuideCard key={g.slug} guide={g} onDownload={handleDownload} />
          ))}
        </div>
      </section>

      {/* Inline CTA */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="bg-[#0F392B] rounded-xl p-8 sm:p-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-white max-w-lg">
            <div className="flex items-center gap-2 mb-3">
              <GraduationCap className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.5} />
              <span className="text-xs font-medium tracking-wide uppercase text-[#D4AF37]" style={{ fontFamily: "'Playfair Display', serif" }}>Ivy League Consulting</span>
            </div>
            <h2 className="text-lg font-semibold mb-2">These guides are just the beginning.</h2>
            <p className="text-sm text-white/70 leading-relaxed">
              The $99 Portal gives you AI essay feedback, college match scoring, deadline tracking, activity optimization, and a 45-minute strategy session with a former admissions officer. Everything in one place.
            </p>
          </div>
          <Link to="/sign-up">
            <Button className="bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-[#0F392B] font-semibold px-6 h-11 text-sm whitespace-nowrap" data-testid="resources-portal-cta">
              Access the Portal — $99 <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Email Capture Stats */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center max-w-md mx-auto">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Mail className="h-4 w-4 text-[#0F392B]" />
            <span className="text-xs text-muted-foreground">Join 2,400+ families who downloaded our guides</span>
          </div>
          <p className="text-xs text-muted-foreground">
            We send 1-2 emails per month with admissions insights and deadline reminders. No spam, ever.
          </p>
        </div>
      </section>

      <MarketingFooter />

      {/* Modals */}
      {activeGuide && (
        <EmailCaptureModal
          guide={activeGuide}
          onClose={() => setActiveGuide(null)}
          onSuccess={handleSuccess}
        />
      )}
      {completedGuide && (
        <DownloadSuccessView
          guide={completedGuide}
          onReset={handleReset}
        />
      )}
    </div>
  );
}
